import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import usePaymentStatus from "../../hooks/usePaymentStatus";
import {
  BILLING_CYCLES,
  formatPlanPrice,
  getPlanDisplayName,
  getStaticPaymentPlans,
} from "../../services/razorpayService";

const PLAN_META = {
  Basic: { id: "basic", level: 1 },
  "Basic Plus": { id: "basic_plus", level: 2 },
  "Basic Pro": { id: "basic_pro", level: 3 },
};

function BillingToggle({ value, onChange }) {
  return (
    <div className="inline-flex rounded-full border border-violet-200 bg-white/80 p-1 shadow-sm">
      {BILLING_CYCLES.map((cycle) => (
        <button
          key={cycle.id}
          type="button"
          onClick={() => onChange(cycle.id)}
          className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
            value === cycle.id
              ? "bg-[#6938EF] text-white shadow-md"
              : "text-[#6938EF] hover:bg-violet-50"
          }`}
        >
          {cycle.label}
        </button>
      ))}
    </div>
  );
}

export const PaymentPage = () => {
  const navigate = useNavigate();
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const token = Cookies.get("auth_token");
  const { planType: planStatus, loading: paymentStatusLoading } = usePaymentStatus();
  const PLANS = useMemo(
    () =>
      getStaticPaymentPlans().map((plan) => ({
        ...plan,
        ...PLAN_META[plan.plan_name],
      })),
    []
  );
  const visiblePlans = useMemo(() => {
    if (planStatus >= 3) {
      return PLANS.filter((plan) => plan.level === 3);
    }
    if (planStatus >= 2) {
      return PLANS.filter((plan) => plan.level > 1);
    }
    return PLANS;
  }, [PLANS, planStatus]);
  const fetchPaymentHistory = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/payment-history`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.status === "1" && data.data?.[0]) {
        setPaymentData(data.data[0]);
      }
    } catch (err) {
      } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPaymentHistory();
  }, [fetchPaymentHistory]);

  const latestPayment = paymentData?.payment_history?.find((payment) => payment.is_active) || paymentData?.payment_history?.[0];
  const isPaid = Boolean(latestPayment?.is_active);
  const priceSuffix = billingCycle === "annual" ? "/ year" : "/ month";

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleSelectPlan = (plan) => {
    navigate(`/payment?plan=${encodeURIComponent(plan.plan_name)}`);
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden p-3 sm:p-5 lg:p-6">
      <div className="shrink-0 rounded-[28px] bg-[#F3F0FF]/95 px-2 pb-4 sm:px-0">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 sm:text-2xl">Payment</h1>
            <h2 className="mt-2 text-lg font-semibold text-gray-900 sm:text-xl">
              Choose Your Plan
            </h2>
            <p className="mt-1 text-sm text-gray-500 sm:text-base">
              Select the perfect plan for your needs.
            </p>
          </div>
          <div className="flex justify-start lg:justify-end">
            <BillingToggle value={billingCycle} onChange={setBillingCycle} />
          </div>
        </div>
      </div>

      {loading || paymentStatusLoading ? (
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#7A5AF8]" />
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1 [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:hidden max-md:pb-[calc(1.5rem+var(--mobile-nav-offset,5rem))]">
          <div className="mx-auto flex max-w-6xl flex-col gap-4 pb-3">
            {visiblePlans.map((plan) => {
              const active = isPaid && plan.level === planStatus;
              const isPopular = plan.id === "basic_plus";
              const price = plan.premium ? formatPlanPrice(plan, billingCycle) : plan.priceLabel;
              const cardTone = isPopular
                ? "border-transparent bg-gradient-to-br from-[#8B5CF6] via-[#6938EF] to-[#3D1E8F] text-white shadow-xl"
                : active
                ? "border-[#7A5AF8] bg-white shadow-[0_18px_45px_rgba(122,90,248,0.16)]"
                : "border-white bg-white shadow-[0_12px_35px_rgba(36,25,80,0.08)]";
              const mutedText = isPopular ? "text-white/80" : active ? "text-[#6B47F5]" : "text-gray-500";
              const featureCard = isPopular
                ? "border-white/15 bg-white/10 text-white"
                : active
                ? "border-[#E5DCFF] bg-[#F8F5FF] text-[#42306B]"
                : "border-gray-100 bg-[#eaeaef] text-gray-700";
              const checkClass = isPopular
                ? "bg-white text-[#6938EF]"
                : "bg-[#7A5AF8] text-white";

              return (
                <article
                  key={plan.id}
                  className={`relative overflow-hidden rounded-[28px] border p-4 transition sm:p-5 lg:p-5 ${cardTone}`}
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className={`text-2xl font-bold sm:text-3xl ${isPopular ? "text-white" : active ? "text-[#5733D4]" : "text-gray-900"}`}>
                          {getPlanDisplayName(plan)}
                        </h3>
                        {active && (
                          <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${isPopular ? "bg-white text-[#6938EF]" : "bg-[#EDE7FF] text-[#6938EF]"}`}>
                            <span>✓</span>
                            Active
                          </span>
                        )}
                        {isPopular && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white">
                            <img src="./crown.svg" alt="" className="h-4 w-4" />
                            Popular
                          </span>
                        )}
                      </div>
                      <p className={`mt-0.5 max-w-3xl text-sm leading-relaxed sm:text-base ${mutedText}`}>
                        {plan.description}
                      </p>
                    </div>

                    <div className="shrink-0 rounded-2xl px-4 py-3 text-left lg:min-w-[180px] lg:text-right">
                      <span
                        className={`text-2xl font-bold sm:text-3xl ${
                          isPopular
                            ? "text-white"
                            : active
                            ? "text-[#5733D4]"
                            : "text-gray-900"
                        }`}
                      >
                        {price}
                      </span>
                      {plan.premium && (
                        <span
                          className={`text-sm font-semibold ${
                            isPopular
                              ? "text-white/85"
                              : active
                              ? "text-[#6B47F5]"
                              : "text-gray-600"
                          }`}
                        >
                          {" "}
                          {priceSuffix}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:gap-2">
                    {plan.features.map((item) => (
                      <div key={item} className={`flex min-w-0 items-center gap-1.5 rounded-2xl border p-2.5 sm:p-2 ${featureCard}`}>
                        <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold ${checkClass}`}>
                          ✓
                        </span>
                        <span className="min-w-0 text-sm font-medium leading-snug break-words">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className={`mt-2 flex flex-col gap-3 border-t pt-2 sm:flex-row sm:items-center sm:justify-between ${isPopular ? "border-white/15" : "border-gray-100"}`}>
                    {active ? (
                      <>
                        <button
                          type="button"
                          className={`w-full cursor-default rounded-2xl px-5 py-2.5 text-sm font-semibold sm:w-max ${isPopular ? "bg-white text-[#6938EF]" : "bg-[#7A5AF8] text-white"}`}
                        >
                          Already on this plan
                        </button>
                        {plan.id !== "basic" && (
                          <div className={`grid gap-1 text-sm font-medium sm:text-right ${isPopular ? "text-white/90" : "text-[#6B47F5]"}`}>
                            <p>
                              Purchased Date: <span>{formatDate(latestPayment?.purchase_date || latestPayment?.created_at || latestPayment?.last_date)}</span>
                            </p>
                            <p>
                              Renew Date: <span>{formatDate(latestPayment?.renew_date)}</span>
                            </p>
                          </div>
                        )}
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleSelectPlan(plan)}
                        className={`w-full rounded-2xl px-5 py-2.5 text-sm font-semibold transition sm:w-max ${isPopular ? "border border-white bg-white/10 text-white hover:bg-white hover:text-[#6938EF]" : "border border-gray-200 bg-white text-gray-900 hover:border-[#7A5AF8] hover:text-[#6938EF]"}`}
                      >
                        {planStatus > 0 && plan.level > planStatus
                          ? `Upgrade to ${getPlanDisplayName(plan)}`
                          : "Select Plan"}
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
